import { useMemo, useState } from 'react'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetIntegrator, usePutIntegrator, type IntegratorDto } from '@dhl/mdp-api'
import { Box, Card, Grid, H6 } from '@dhl-ui/components'
import { links } from '~/AppRoutes'
import { useNotificationMessage } from '~/common'
import { DangerZoneCard } from '~/common/components/DangerZoneCard'
import {
  CancelDialog,
  createValidationSchema,
  IntegratorDangerZoneDialog,
  IntegratorForm,
} from '~/modules/integrators/components'
import type { Integrators } from '~/modules/integrators/model'
import { BackButton } from '~/temp-ds-component/BackButton'

type Params = {
  id: string
}

export const IntegratorEditPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { showError, showSuccess } = useNotificationMessage()
  const { id } = useParams<Params>()

  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false)
  const [dangerZoneOpen, setDangerZoneOpen] = useState<boolean>(false)
  const validationSchema = createValidationSchema(t)

  const { data: integrator } = useGetIntegrator(id!)
  const { mutate: editIntegrator, isPending } = usePutIntegrator()

  const initialValues: Integrators = useMemo(
    () => ({
      id: integrator?.id ?? '',
      name: integrator?.name ?? '',
      description: integrator?.description ?? '',
      limitedTrackTrace: integrator?.limitedTrackTrace ?? false,
    }),
    [integrator]
  )

  if (!id) {
    return null
  }

  const onBack = (dirty: boolean) => {
    dirty ? setCancelDialogOpen(true) : navigate(id ? links.integratorDetailsLink(id) : links.integratorsLink())
  }

  const onClickOpen = () => {
    setDangerZoneOpen(true)
  }

  const onSubmit = (integrator: IntegratorDto) => {
    editIntegrator(
      {
        data: integrator,
      },
      {
        onSuccess: () => {
          showSuccess(t('admin.integratorsPage.updateIntegrator'))
          navigate(links.integratorsLink())
        },
        onError: (response) => {
          showError(response?.message ?? t('admin.integratorsPage.errorMessage'))
        },
      }
    )
  }

  const dangerZoneContent = {
    title: t('admin.integratorsPage.dangerZoneTitle'),
    subtitle: t('admin.integratorsPage.deleteButtonTitle'),
    value: t('admin.integratorsPage.dangerZoneMessage'),
    button: t('admin.integratorsPage.dangerZoneHeader'),
    id,
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        validateOnChange={false}
        enableReinitialize
      >
        {({ dirty }) => (
          <>
            <Grid>
              <Box marginBottom={2}>
                <BackButton onClick={() => onBack(dirty)}>{t('admin.integratorsPage.backButtonTitle')}</BackButton>
              </Box>
            </Grid>
            <Grid size={{ xs: 9 }}>
              <Card>
                <Card.Header title={t('admin.integratorsPage.titleText')} />
                <Card.Content>
                  <H6>{t('admin.integratorsPage.accountInformation')}</H6>
                  <IntegratorForm disabled={true} onCancel={() => onBack(dirty)} />
                </Card.Content>
              </Card>
            </Grid>
            <Grid size={{ xs: 9 }}>
              <Box marginTop={2}>
                <DangerZoneCard dangerZoneContent={dangerZoneContent} onClickOpen={onClickOpen} />
              </Box>
            </Grid>
          </>
        )}
      </Formik>
      <CancelDialog open={cancelDialogOpen} onCancel={() => setCancelDialogOpen(false)} />
      {id && (
        <IntegratorDangerZoneDialog
          open={dangerZoneOpen}
          id={id}
          onClose={() => setDangerZoneOpen(false)}
          onDeleteComplete={() => onBack(false)}
        />
      )}
    </>
  )
}
